import React, { useState, useEffect, useContext } from 'react'
import { object, bool, func } from 'prop-types'
import styled, { css } from 'styled-components'
import { stripUnit } from 'polished'
import { Link } from 'gatsby'
import { pricify } from 'utils/pricify'
import { sum } from 'lodash'

import { SETTINGS_FOR_PRODUCT_NAME } from 'utils/constants'
import CheckoutContext from 'contexts/Checkout'
import NativeAuthContext from 'components/NativeAuth'

import * as spacing from 'styles/spacing'
import { media, mediaDown } from 'styles/media'
import { fontSizes, fontWeights } from 'styles/typography'
import { white, black, green, red, greyLight } from 'styles/colors'
import { cover, animationTime, animationCurve, borderRadius } from 'styles/global'
import { availableForSale } from 'utils/products'

import Button from 'components/button/Button'
import QuantityPicker from 'components/QuantityPicker'
import Toggle from 'components/Toggle'
import Tooltip from 'components/Tooltip'
import Icon from 'components/Icon'
import Modal, { UnavailableTag } from 'components/buy/CartridgeModal'

import InfoCircleIcon from 'icons/info-circle.svg'

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${white};
  box-shadow: 0px 3px 9px rgba(0, 0, 0, 0.11);
`

const Figure = styled(Link)`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: ${white};
  padding: ${spacing.large};

  ${media.large`
    padding: ${spacing.xLarge};
  `};
`

const SwatchWrap = styled.div`
  position: relative;
  width: 60%;

  &:after {
    content: '';
    display: block;
    padding-bottom: 100%;
  }

  ${media.medium`
    width: 65%;
    max-width: 180px;
    margin-bottom: ${spacing.medium};
  `};

  ${media.large`
    width: 75%;
    margin-bottom: ${stripUnit(spacing.medium) * 1.25 + 'px'};
  `};
`

const Swatch = styled.img`
  object-fit: cover;
  ${cover('absolute')};
  transition: filter ${animationTime} ${animationCurve};

  ${Figure}:hover & {
    filter: drop-shadow(3px 6px 20px rgba(0, 0, 0, 0.3));
  }
`

const Name = styled.span`
  display: block;
  text-transform: uppercase;
  color: ${white};
  letter-spacing: 0.15em;

  ${props =>
    props.isDark &&
    css`
      color: ${black};
    `}

  ${mediaDown.medium`
    display: none;
  `};
`

const Content = styled.div`
  padding: ${spacing.medium};
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;

  ${media.small`
    padding: ${spacing.large};
  `};

  ${media.large`
    padding-bottom: ${spacing.xLarge};
  `};
`

const Details = styled.span`
  position: absolute;
  top: ${spacing.medium};
  right: ${spacing.medium};
  color: ${white};
  font-size: ${fontSizes.tiny};

  ${props =>
    props.isDark &&
    css`
      color: ${black};
    `}

  ${mediaDown.medium`
    display: none;
  `};

  ${media.large`
    top: ${stripUnit(spacing.medium) * 1.25 + 'px'};
    right: ${stripUnit(spacing.medium) * 1.25 + 'px'};
  `};
`

const TitleWrap = styled.div`
  margin-bottom: ${spacing.small};

  ${media.medium`
    display: flex;
    margin-bottom: ${stripUnit(spacing.small) * 1.5 + 'px'};
  `};

  ${media.large`
    margin-bottom: ${spacing.medium};
  `};
`

const Title = styled.strong`
  display: block;
  color: ${black};

  ${mediaDown.medium`
    text-align: center;
    font-size: ${fontSizes.small};
  `};

  ${media.medium`
    flex: 1;
    padding-right: ${spacing.medium};
  `};
`

const Price = styled.span`
  color: ${black};
  font-weight: ${fontWeights.medium};
`

const Subscription = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing.large};

  ${mediaDown.medium`
    display: none;
  `};
`

const SubscriptionLabel = styled.span`
  display: flex;
  align-items: center;
  font-size: ${fontSizes.tiny};
  color: ${black};
  font-weight: ${fontWeights.medium};
  padding-right: ${spacing.small};
  flex: 1;
`

const Description = styled.p`
  font-size: ${fontSizes.tiny};

  ${mediaDown.medium`
    display: none;
  `};
`

const Label = styled.span`
  display: block;
  font-size: ${fontSizes.tiny};
  color: ${black};
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing.medium};

  ${props =>
    props.disabled &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `};
`

const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: auto;
`

const AddButton = styled(Button)`
  ${media.small`
    width: 154px;
  `};

  ${mediaDown.small`
    padding: 0;
    width: 100%;
  `};
`

const Tag = styled.span`
  position: absolute;
  top: ${spacing.small};
  left: ${spacing.small};
  display: inline-block;
  background-color: ${red};
  color: ${white};
  text-transform: uppercase;
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.tiny};
  padding: 2px 6px;
  border-radius: ${borderRadius};
  min-width: 56px;
  text-align: center;

  ${props =>
    props.green &&
    css`
      background-color: ${green};
    `}

  ${media.small`
    top: ${stripUnit(spacing.small) * 1.5 + 'px'};
    left: ${stripUnit(spacing.small) * 1.5 + 'px'};
  `};

  ${media.medium`
    top: ${spacing.medium};
    left: ${spacing.medium};
    padding: 3px 8px;
    min-width: 96px;
  `};
`

const Strengths = styled.div`
  margin-bottom: ${spacing.medium};
  flex-shrink: 0;
`

const StrengthsList = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin: -${spacing.tiny};

  ${media.small`
    margin: -${stripUnit(spacing.tiny) * 1.5 + 'px'};
  `};
`

const StrengthsLabel = styled.span`
  display: block;
  font-size: 8px;
  color: ${black};
  text-align: center;
  margin-bottom: ${spacing.tiny};

  ${mediaDown.small`
    margin-left: -${spacing.small};
    margin-right: -${spacing.small};
  `};

  ${media.small`
    margin-bottom: ${spacing.small};
  `};
`

const Strength = styled.span`
  display: block;
  letter-spacing: 0.1em;
  color: ${black};
  font-weight: ${fontWeights.medium};
  padding: ${spacing.tiny};
  font-size: ${fontSizes.small};

  ${props =>
    props.disabled &&
    css`
      opacity: 0.5;
    `}

  ${media.small`
    padding: ${stripUnit(spacing.tiny) * 1.5 + 'px'};
  `};

  ${media.medium`
    padding: 0;
  `};

  ${mediaDown.medium`
    font-size: 11px;
  `};
`

const Info = styled.p`
  font-size: ${fontSizes.tiny};
`

const InfoCircle = styled.div`
  color: ${greyLight};
  display: flex;

  > * {
    width: 12px !important;
    height: 12px !important;
  }
`

const subscription = 'subscription'

const CartridgeThumbnail = ({ product, collectionHandle, subscriptionOnly, hidden }) => {
  const { addLineItemsToCart, setTinyCartVisible, products } = useContext(CheckoutContext)
  const { province } = useContext(NativeAuthContext)

  const [cartridge, setCartridge] = useState(product)
  const [price, setPrice] = useState(pricify({ amount: 0 }))
  const [loading, setLoading] = useState(false)
  const [isSubscription, setIsSusbscription] = useState(subscriptionOnly || false)
  const [modalVisible, setModalVisible] = useState(false)

  const fetchedProduct = products.find(p => product.id.includes(p.shopifyGid))

  const productHandle = SETTINGS_FOR_PRODUCT_NAME[product.handle]

  useEffect(() => {
    document.body.style.overflow = modalVisible ? 'hidden' : 'visible'
  }, [modalVisible])

  useEffect(() => {
    setPrice(
      pricify({ amount: sum(cartridge.variants.map(v => v.quantity)) * cartridge.priceRange.maxVariantPrice.amount })
    )
  }, [cartridge])

  const handleQuantityChange = (value, title, variantId) => {
    const upToDateCartridge = { ...cartridge }
    const variantIndex = cartridge.variants.findIndex(v => v.id === variantId)
    const quantity = cartridge.variants[variantIndex].quantity
    upToDateCartridge.variants[variantIndex].quantity = quantity + value
    setCartridge(upToDateCartridge)
  }

  const onAdd = async (product, localVariants) => {
    setLoading(true)
    const variants = localVariants || cartridge.variants

    let lineItems = variants
      .filter(v => v.quantity > 0)
      .map(v => ({
        variantId: v.id,
        quantity: v.quantity,
        name: v.title,
        price: v.price,
        product,
        variant: v,
        title: v.title,
        customAttributes: isSubscription
          ? [
              {
                key: 'orderType',
                value: subscription
              }
            ]
          : []
      }))

    await addLineItemsToCart(lineItems)
    setTinyCartVisible(true)
    setTimeout(() => {
      setTinyCartVisible(false)
    }, 3000)

    if (!subscriptionOnly) {
      setIsSusbscription(false)
    }

    setCartridge({
      ...cartridge,
      variants: cartridge.variants.map(v => ({ ...v, quantity: 0 }))
    })
    setLoading(false)
  }

  return (
    <>
      <Container hidden={hidden}>
        <Figure
          to={`/buy-online/${collectionHandle}/${product.handle}/${subscriptionOnly ? '?subscriptionMode' : ''}`}
          css={`
            background: ${productHandle?.gradient};
          `}
        >
          {productHandle?.refillsTag && (
            <Tag green={productHandle.refillsTag === 'Zero Nic Available'}>{productHandle.refillsTag}</Tag>
          )}
          <SwatchWrap>
            <Swatch src={cartridge.images[0].originalSrc} alt={product.title} />
          </SwatchWrap>
          <Name isDark={productHandle?.isDark}>{productHandle?.name || cartridge.title}</Name>
          <Details isDark={productHandle?.isDark}>See details</Details>
        </Figure>

        <Content>
          <TitleWrap>
            <Title>{cartridge.title}</Title>
            <Price className="u-hide-medium-down">{price}</Price>
          </TitleWrap>
          <Description>{cartridge.description}</Description>

          <StrengthsLabel className="u-hide-medium-up">Nicotine Strength Available (w/w)</StrengthsLabel>

          <Strengths className="u-hide-medium-up">
            <StrengthsList>
              {cartridge.variants.map((variant, i) => {
                const fetchedVariant = fetchedProduct?.variants.find(v => variant.id.includes(v.shopifyGid))
                if (fetchedVariant && !fetchedVariant.availableProvinces.includes(province)) return null
                const isAvailableForSale = availableForSale({ variant: fetchedVariant, province })
                return (
                  <Strength key={i} disabled={!isAvailableForSale}>
                    {variant.title}
                  </Strength>
                )
              })}
            </StrengthsList>
          </Strengths>

          <div className="u-hide-medium-down">
            <Row>
              <Label>Nicotine strength</Label>
              <Label
                css={`
                  width: 108px;
                  text-align: center;
                `}
              >
                Pack quantity
              </Label>
            </Row>

            <div>
              {cartridge.variants.map((variant, i) => {
                const fetchedVariant = fetchedProduct?.variants.find(v => variant.id.includes(v.shopifyGid))

                if (fetchedVariant && !fetchedVariant.availableProvinces.includes(province)) return null
                const isAvailableForSale = availableForSale({ variant: fetchedVariant, province })

                return (
                  <Row key={i}>
                    <Strength disabled={!isAvailableForSale}>{variant.title}</Strength>
                    {!isAvailableForSale && <UnavailableTag>Unavailable</UnavailableTag>}
                    <QuantityPicker
                      value={variant.quantity}
                      onChange={handleQuantityChange}
                      disabled={!isAvailableForSale}
                      isAvailableForSale={isAvailableForSale}
                      product={variant}
                    />
                  </Row>
                )
              })}
            </div>
          </div>

          {!subscriptionOnly && (
            <Subscription>
              <SubscriptionLabel>
                <span
                  css={`
                    margin-right: ${spacing.small};
                  `}
                >
                  Make it a subscription and save big?
                </span>
                <Tooltip
                  content={
                    <Info>
                      Buy <strong>3-4 packs</strong> of any flavour & <strong>save 20%</strong> or{' '}
                      <strong>Buy 5 or above</strong> of any flavour & <strong>save 25%</strong>. Auto-shipped and
                      billed every 30 days. Free shipping on all subscription orders.
                    </Info>
                  }
                >
                  <InfoCircle>
                    <Icon icon={<InfoCircleIcon />} />
                  </InfoCircle>
                </Tooltip>
              </SubscriptionLabel>
              <Toggle
                checked={isSubscription}
                onClick={() => {
                  setIsSusbscription(!isSubscription)
                }}
              />
            </Subscription>
          )}

          <Footer>
            {/* Desktop */}
            <AddButton
              className="u-hide-medium-down"
              theme="primary"
              onClick={onAdd}
              disabled={sum(cartridge.variants.map(v => v.quantity)) === 0 || loading}
              isLoading={loading}
            >
              Add
            </AddButton>

            {/* Mobile */}
            <AddButton
              className="u-hide-medium-up"
              theme="secondary"
              onClick={() => {
                setModalVisible(!modalVisible)
              }}
            >
              Add - {pricify(cartridge.priceRange.maxVariantPrice)}
            </AddButton>
          </Footer>
        </Content>
      </Container>
      {modalVisible && (
        <Modal
          name={cartridge.title}
          product={cartridge}
          variants={cartridge.variants}
          handle={cartridge.handle}
          description={cartridge.description}
          buttonLabel="Add to cart"
          close={() => {
            setModalVisible(false)
          }}
          showSubscription
          onAdd={onAdd}
          onToggleClick={() => {
            setIsSusbscription(!isSubscription)
          }}
          toggleChecked={isSubscription}
        />
      )}
    </>
  )
}

CartridgeThumbnail.propTypes = {
  product: object.isRequired,
  subscriptionOnly: bool,
  hidden: bool,
  handleQuantityChange: func
}

export default CartridgeThumbnail
